import { useMemo, useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Button from '@components/Button';
import { ModalBase } from '@components/Modal';
import { Chip } from '@components/Chip';
import { FiltersFormCreator } from '@components/FiltersFormCreator';
import { FiltersIcon } from 'assets/images/icons';
import { GroupByFilter } from '@constants/analytics';
import { updateState, getChipsLabel, createUpdatedChips } from '@helpers/filters';
import { useTranslation, useSocialPostsPlatforms, useLocations } from '@hooks';
import { ProfileName } from '@constants/integrations';

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        width: 564,
        '@media (max-width: 1024px)': {
            width: '100%',
            height: '100%',
            borderRadius: 0,
            maxHeight: 'none',
        },
    },
    '& .modal-base-content': {
        padding: 16,
    },
});

export const FiltersWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: '8px',
    '@media print': {
        '& button': {
            display: 'none',
        },
    },
});

const FiltersPanel = ({
    setFiltersOpened,
    filtersNumber,
    filterOpened,
    onApplyFilters,
    disabled = false,
    filter,
    setFilter,
    withChips = true,
    type,
    setFilterNumber,
}) => {
    const firstRender = useRef(true);
    const { t } = useTranslation();
    const [filterChips, setFilterChips] = useState({
        locations: [],
        platforms: [],
        timeframe: {},
    });
    const [refetchFilters, setRefetchFilters] = useState(false);

    const isMobile = useMediaQuery('@media (max-width: 600px)');
    const { getPlatformsOptions, getConnectedPlatforms } = useSocialPostsPlatforms();
    const { getLocationsOptions } = useLocations();

    useEffect(() => {
        if (refetchFilters) {
            setRefetchFilters(false);
            onApplyFilters();
        }
    }, [refetchFilters]);

    useEffect(() => {
        if (firstRender.current && filter.locations.length > 0) {
            applyChips();
            firstRender.current = false;
        }
    }, [filter]);

    const options = useMemo(() => {
        //locationsOptions
        const filterCb = l =>
            l.profiles?.some(p => p.type === ProfileName.GoogleOfficial || p.type === ProfileName.FacebookOfficial);
        const locationsOptions = getLocationsOptions(filterCb);

        //platformsOptions
        const platformsOptions = getPlatformsOptions(filter.locations.map(item => item.id));

        //groupingOptions
        const groupingOptions = [
            { value: GroupByFilter.Days, label: t('common.day') },
            { value: GroupByFilter.Weeks, label: t('common.week') },
            { value: GroupByFilter.Months, label: t('common.month') },
            { value: GroupByFilter.Years, label: t('common.year') },
        ];

        return {
            locations: locationsOptions,
            platforms: platformsOptions,
            grouping: groupingOptions,
        };
    }, [filter]);

    useEffect(() => {
        const allFilters = { ...filter, ...filterChips };
        const filterNumber = Object.keys(allFilters).reduce((total, key) => {
            let increment = 0;

            switch (key) {
                case 'timeframe':
                    increment = filterChips[key]?.start ? 1 : 0;
                    break;
                default:
                    increment = filterChips[key]?.length > 0 ? 1 : 0;
                    break;
            }

            return total + increment;
        }, 0);

        setFilterNumber(filterNumber);
    }, [filterChips, filter]);

    const update = {
        locations: items => {
            const selectedValues = items.length ? items : [];
            const selectedValuesIds = selectedValues.length ? selectedValues.map(item => item.id) : [];
            const isAllPlatformsSelected = filter.platforms.length === options.platforms.length;
            const platforms = getConnectedPlatforms(selectedValuesIds);

            setFilter(prev => ({
                ...prev,
                locations: selectedValues,
                platforms: updateState(prev?.platforms, platforms, isAllPlatformsSelected),
            }));
        },
        platforms: value => setFilter(prev => ({ ...prev, platforms: value })),
        timeframe: data => {
            setFilter(prev => ({ ...prev, timeframe: { start: data[0], end: data[1] } }));
        },
        grouping: e => {
            setFilter(prev => ({ ...prev, grouping: e.target.value }));
        },
    };

    const CHIPS_ACTIONS = {
        locations: {
            onDelete: () => {
                update.locations(options.locations);
                setFilterChips(prev => ({ ...prev, locations: [] }));
            },
            value: getChipsLabel(filterChips?.locations),
        },
        platforms: {
            onDelete: () => {
                setFilter(prev => ({
                    ...prev,
                    platforms: getConnectedPlatforms(filter?.locations?.map(l => l.id)),
                }));
                setFilterChips(prev => ({ ...prev, platforms: [] }));
            },
            value: getChipsLabel(filterChips?.platforms),
        },
        timeframe: filterChips?.timeframe?.start
            ? {
                  onDelete: () => {
                      setFilter(prev => ({ ...prev, timeframe: { start: null, end: null } }));
                      setFilterChips(prev => ({ ...prev, timeframe: { start: null, end: null } }));
                  },
                  value: `${format(new Date(filterChips.timeframe.start), 'dd.MM.yyyy')} - ${format(
                      new Date(filterChips.timeframe.end),
                      'dd.MM.yyyy',
                  )}`,
              }
            : { onDelete: () => {}, value: '' },
    };

    const applyChips = () => {
        const updatedChips = createUpdatedChips(filterChips, filter, options);
        setFilterChips(updatedChips);
    };

    const createFormOptions = (filter, update, options) => {
        const formOptions = [];

        for (let key in filter) {
            if (Array.isArray(filter[key])) {
                formOptions.push({
                    type: key === 'locations' ? 'autocomplete' : 'multiSelect',
                    label: t(`filters.${key}`),
                    value: filter[key],
                    onChange: update[key],
                    options: options[key],
                    countable: true,
                    error: !filter[key].length,
                });
            } else if (key === 'timeframe') {
                formOptions.push({
                    type: 'timeframe',
                    label: t(`filters.${key}`),
                    value: [filter[key].start, filter[key].end],
                    onChange: update[key],
                });
            } else if (key === 'grouping') {
                formOptions.push({
                    type: 'select',
                    label: t(`filters.${key}`),
                    value: filter[key],
                    onChange: update[key],
                    options: options[key],
                });
            }
        }

        return formOptions;
    };

    const isValid = Object.keys(filter).every(key => {
        if (Array.isArray(filter[key])) {
            return !!filter[key].length;
        } else {
            return true;
        }
    });

    return (
        <FiltersWrapper>
            <Button
                variant="outlined"
                onClick={() => setFiltersOpened(true)}
                startIcon={<FiltersIcon />}
                disabled={disabled}
                sx={{ marginRight: '8px' }}
            >
                {t('buttons.filters')}
                {filtersNumber > 0 ? ` (${filtersNumber})` : ''}
            </Button>
            {withChips &&
                !isMobile &&
                Object.keys(filterChips).map(
                    key =>
                        (!!filterChips[key]?.length || filterChips[key]?.start) && (
                            <Chip
                                key={key}
                                label={CHIPS_ACTIONS[key].value}
                                onDelete={() => {
                                    CHIPS_ACTIONS[key].onDelete();
                                    setRefetchFilters(true);
                                }}
                            />
                        ),
                )}
            <Modal
                isOpen={filterOpened}
                handleClose={() => setFiltersOpened(false)}
                title={t('buttons.filters')}
                onPrimaryAction={() => {
                    applyChips();
                    setRefetchFilters(true);
                    setFiltersOpened(false);
                }}
                primaryActionDisabled={!isValid}
                primaryActionText={t('buttons.applyFilters')}
                onSecondaryAction={() => setFiltersOpened(false)}
                secondaryActionText={t('buttons.cancel')}
            >
                <FiltersFormCreator type={type} options={createFormOptions(filter, update, options)} />
            </Modal>
        </FiltersWrapper>
    );
};

export default FiltersPanel;
